import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  PaperPlaneTilt, Sparkle, Robot, User, ArrowRight, 
  Lightning, Lightbulb, Package, Calendar, Copy, TrendUp,
  Spinner, Trash, CaretDown, CaretUp, Browser, Envelope,
  ChartLineUp, Gear
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AskPayGBot = ({ 
  user, 
  onToolAction,
  className = '',
  defaultExpanded = true,
  initialMessage = ''
}) => {
  const [message, setMessage] = useState(initialMessage || '');
  const [isLoading, setIsLoading] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isMobile, setIsMobile] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Scroll to bottom with delay for mobile
  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }, 100);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [conversation, scrollToBottom]);

  // Load chat history on mount
  useEffect(() => {
    loadChatHistory();
  }, []);

  // 2026-07-23 — deep-link prompt prefill.
  // `AskPayGBotPage` passes the `?prompt=...` query param
  // straight through as `initialMessage`. We keep the input in
  // sync whenever that prop changes (e.g. user navigates from
  // one task chip to another without unmounting the component).
  // ALSO: proactively wipe the legacy
  // `ask_paygbot__pending_prompt` localStorage key that a
  // previous iteration used for the same handoff, so stale
  // browsers don't keep the key around.
  useEffect(() => {
    if (initialMessage) {
      setMessage(initialMessage);
    }
    try {
      window.localStorage.removeItem('ask_paygbot__pending_prompt');
    } catch {
      // no-op if storage is unavailable
    }
  }, [initialMessage]);

  const loadChatHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/chat/history?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        return;
      }

      // Read the body ONCE as text, then parse. Response.clone() throws
      // a DOMException ("The object can not be cloned") in some browsers
      // on HTTP/2 responses with set-cookie / Cloudflare caching — see
      // regression fix 2026-04-30.
      let data = null;
      try {
        const raw = await response.text();
        data = raw ? JSON.parse(raw) : null;
      } catch (parseErr) {
        console.error('[AskPayGBot] Failed to parse history body:', parseErr);
        return;
      }

      if (data?.history?.length > 0) {
        const conv = [];
        data.history.forEach(h => {
          conv.push({ role: 'user', content: h.message });
          conv.push({ role: 'assistant', content: h.response, suggested_action: h.suggested_action });
        });
        setConversation(conv.slice(-10));
      }
    } catch (error) {
      console.error('[AskPayGBot] Failed to load chat history:', error);
    }
  };

  const handleSend = async () => {
    if (!message.trim() || isLoading) return;

    const userMessage = message.trim();
    setMessage('');
    setIsLoading(true);

    // Add user message to conversation
    setConversation(prev => [...prev, { role: 'user', content: userMessage }]);
    
    // Scroll after adding user message
    setTimeout(scrollToBottom, 50);

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/chat/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: userMessage,
          conversation_id: conversationId
        })
      });

      // PRODUCTION REGRESSION FIX 2026-04-30:
      // Read body ONCE as text, then parse. Response.clone() was throwing
      // a DOMException "The object can not be cloned" in some browsers
      // (HTTP/2 responses with set-cookie cookies from Cloudflare), and
      // that raw error string was bubbling up to the user as a toast.
      //
      // Note: response.text() can only be called once — that's why we
      // capture the raw text first and then do ALL our parsing /
      // fallback logic on the captured string, not on the Response object.
      let rawBody = '';
      try {
        rawBody = await response.text();
      } catch (readErr) {
        console.error('[AskPayGBot] Failed to read response body:', readErr);
        throw new Error('We could not read the response from the server. Please try again.');
      }

      let data = null;
      if (rawBody) {
        try {
          data = JSON.parse(rawBody);
        } catch (parseErr) {
          console.error('[AskPayGBot] Non-JSON response:', rawBody.slice(0, 200));
          data = null;
        }
      }

      if (!response.ok) {
        // Prefer the backend's `detail` message; if the body wasn't JSON
        // (e.g. Cloudflare 520 HTML page), fall back to a plain string.
        const backendMsg =
          (data && (data.detail || data.message))
          || (response.status === 429 && "You've reached your daily limit. Please try again tomorrow.")
          || (response.status >= 500 && 'The server is having a moment. Please try again shortly.')
          || 'Something went wrong while sending your message. Please try again.';
        throw new Error(typeof backendMsg === 'string' ? backendMsg : 'Something went wrong.');
      }

      if (!data) {
        throw new Error('The server returned an empty response. Please try again.');
      }
      
      if (data.conversation_id) {
        setConversationId(data.conversation_id);
      }

      // Add assistant response with quick actions
      setConversation(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        suggested_action: data.suggested_action,
        quick_actions: data.quick_actions || []
      }]);

      // Scroll after assistant response
      setTimeout(scrollToBottom, 100);

    } catch (error) {
      console.error('[AskPayGBot] Error:', error);
      
      const errorMsg = error.message?.includes('Body') || error.message?.includes('locked') || error.message?.includes('disturbed')
        ? 'Something went wrong while sending your message. Please try again.'
        : (error.message || 'Something went wrong while sending your message. Please try again.');
      
      toast.error(errorMsg);
      setConversation(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
      // Don't auto-focus on mobile to prevent keyboard issues
      if (!isMobile) {
        inputRef.current?.focus();
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleActionClick = (action, messageContent) => {
    if (onToolAction) {
      // Pass the chatbot's message context so the target tool can pre-fill
      onToolAction({ ...action, chatContext: messageContent });
    }
  };

  const clearHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/chat/history`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setConversation([]);
      setConversationId(null);
      toast.success('Chat history cleared');
    } catch (error) {
      toast.error('Failed to clear history');
    }
  };

  const getActionIcon = (tool, iconName = null) => {
    if (iconName) {
      switch (iconName) {
        case 'sparkle': return <Sparkle size={16} weight="fill" />;
        case 'lightbulb': return <Lightbulb size={16} weight="fill" />;
        case 'package': return <Package size={16} weight="fill" />;
        case 'calendar': return <Calendar size={16} weight="fill" />;
        case 'copy': return <Copy size={16} weight="fill" />;
        case 'trend_up': return <TrendUp size={16} weight="fill" />;
        case 'browser': return <Browser size={16} weight="fill" />;
        case 'envelope': return <Envelope size={16} weight="fill" />;
        case 'lightning': return <Lightning size={16} weight="fill" />;
        case 'chart': return <ChartLineUp size={16} weight="fill" />;
        case 'settings': return <Gear size={16} weight="fill" />;
        default: break;
      }
    }
    
    switch (tool) {
      case 'content_generation': return <Sparkle size={16} weight="fill" />;
      case 'viral_ideas': return <Lightbulb size={16} weight="fill" />;
      case 'build_offer': return <Package size={16} weight="fill" />;
      case 'weekly_plan': return <Calendar size={16} weight="fill" />;
      case 'clone_viral': return <Copy size={16} weight="fill" />;
      case 'monetization_plan': return <TrendUp size={16} weight="fill" />;
      case 'sales_page': return <Browser size={16} weight="fill" />;
      case 'email_campaign': return <Envelope size={16} weight="fill" />;
      case 'improve_hook': return <Lightning size={16} weight="fill" />;
      case 'score_content': return <ChartLineUp size={16} weight="fill" />;
      default: return <Lightning size={16} weight="fill" />;
    }
  };

  const detectQuickActionsFromText = (text) => {
    if (!text) return [];
    
    const lowerText = text.toLowerCase();
    const actions = [];
    
    const patterns = [
      {
        keywords: ['build your offer', 'create an offer', 'digital product', 'sellable', 'first offer', 'build this offer', 'something to sell', 'build my offer'],
        action: { type: 'open_tool', tool: 'build_offer', label: 'Build My Offer', icon: 'package' }
      },
      {
        keywords: ['create content', 'generate content', 'viral post', 'write a post', 'create a post', 'script for', 'create my first post'],
        action: { type: 'open_tool', tool: 'content_generation', label: 'Create My First Post', icon: 'sparkle' }
      },
      {
        keywords: ['improve your hook', 'better hook', 'hook optimization', 'stronger hook'],
        action: { type: 'open_tool', tool: 'improve_hook', label: 'Improve My Hook', icon: 'lightning' }
      },
      {
        keywords: ['viral ideas', 'content ideas', 'find ideas', 'brainstorm', 'topic ideas'],
        action: { type: 'open_tool', tool: 'viral_ideas', label: 'Generate Viral Ideas', icon: 'lightbulb' }
      },
      {
        keywords: ['plan your week', 'content calendar', 'weekly schedule', 'plan my week'],
        action: { type: 'open_tool', tool: 'weekly_plan', label: 'Plan My Week', icon: 'calendar' }
      },
      {
        keywords: ['monetization plan', 'income roadmap', 'make money from', 'income plan'],
        action: { type: 'open_tool', tool: 'monetization_plan', label: 'Create Income Plan', icon: 'trend_up' }
      },
      {
        keywords: ['sales page', 'landing page', 'product page', 'build my product page'],
        action: { type: 'open_tool', tool: 'sales_page', label: 'Build My Product Page', icon: 'browser' }
      },
      {
        keywords: ['email campaign', 'email sequence', 'set up my funnel'],
        action: { type: 'open_tool', tool: 'email_campaign', label: 'Set Up My Funnel', icon: 'envelope' }
      }
    ];
    
    for (const pattern of patterns) {
      for (const keyword of pattern.keywords) {
        if (lowerText.includes(keyword)) {
          if (!actions.find(a => a.tool === pattern.action.tool)) {
            actions.push(pattern.action);
          }
          break;
        }
      }
      if (actions.length >= 2) break; // Max 2 actions for cleaner UI
    }
    
    return actions;
  };

  const getQuickActionsForMessage = (msg) => {
    if (msg.quick_actions?.length > 0) {
      return msg.quick_actions.slice(0, 2); // Max 2 for cleaner UI
    }
    if (msg.suggested_action) {
      return [msg.suggested_action];
    }
    return detectQuickActionsFromText(msg.content).slice(0, 2);
  };

  const quickPrompts = [
    "What should I work on today?",
    "Help me choose a business model",
    "Give me content ideas for my niche",
    "How do I make my first $1,000?"
  ];

  return (
    <div 
      className={`bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-white/10 rounded-2xl overflow-hidden flex flex-col ${className}`} 
      data-testid="ask-paygbot"
    >
      {/* Header */}
      <div 
        className="px-5 py-4 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border-b border-white/10 cursor-pointer flex-shrink-0"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
              <Robot size={22} className="text-white" weight="fill" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Ask PayGBot</h3>
              <p className="text-slate-400 text-sm">Your AI business advisor</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {conversation.length > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); clearHistory(); }}
                className="p-2 text-slate-400 hover:text-white transition-colors"
                title="Clear history"
              >
                <Trash size={18} />
              </button>
            )}
            <button className="p-2 text-slate-400 hover:text-white transition-colors">
              {isExpanded ? <CaretUp size={20} /> : <CaretDown size={20} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex flex-col flex-1 min-h-0"
          >
            {/* Messages Area — Round 106 (2026-02-14): on mobile
                the chat now fills the available parent height so
                the visible conversation isn't crammed into ~7rem
                (`h-72`). Round 107 (2026-02-14): grows via flex-1
                so it uses ALL available vertical space when the
                parent is height-constrained (e.g. AskPayGBotPage
                `h-[100dvh] flex flex-col`). Desktop keeps the
                pre-existing card sizing via the min-h ceiling. */}
            <div 
              ref={messagesContainerRef}
              className="flex-1 min-h-[240px] md:min-h-[320px] max-h-[70dvh] overflow-y-auto p-4 space-y-4 overscroll-contain"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {conversation.length === 0 ? (
                <div className="text-center py-6">
                  <Robot size={40} className="text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400 mb-3 text-sm">
                    Hi{user?.name ? `, ${user.name.split(' ')[0]}` : ''}! I'm PayGBot.
                  </p>
                  <p className="text-slate-500 text-xs mb-4">
                    Ask me anything about building your business.
                  </p>
                  
                  {/* Quick Prompts */}
                  <div className="space-y-2">
                    {quickPrompts.map((prompt, idx) => (
                      <button
                        key={idx}
                        onClick={() => setMessage(prompt)}
                        className="block w-full text-left px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-slate-300 text-sm transition-colors"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                conversation.map((msg, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[88%] ${msg.role === 'user' ? 'order-2' : 'order-1'}`}>
                      <div className={`flex items-start space-x-2 ${msg.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          msg.role === 'user' 
                            ? 'bg-blue-500' 
                            : 'bg-gradient-to-br from-blue-500 to-purple-500'
                        }`}>
                          {msg.role === 'user' ? (
                            <User size={14} className="text-white" weight="fill" />
                          ) : (
                            <Robot size={14} className="text-white" weight="fill" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className={`px-3 py-2.5 rounded-2xl ${
                            msg.role === 'user'
                              ? 'bg-blue-600 text-white'
                              : 'bg-white/10 text-slate-200'
                          }`}>
                            <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                          </div>
                          
                          {/* Quick Action Buttons - Simplified for mobile */}
                          {msg.role === 'assistant' && (() => {
                            const quickActions = getQuickActionsForMessage(msg);
                            return quickActions.length > 0 ? (
                              <div className="mt-2 flex flex-col gap-2">
                                {quickActions.map((action, actionIdx) => (
                                  <motion.button
                                    key={actionIdx}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: actionIdx * 0.1 }}
                                    onClick={() => handleActionClick(action, msg.content)}
                                    data-testid={`quick-action-${action.tool || action.path}`}
                                    className="flex items-center justify-between w-full px-3 py-2.5 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-xl text-blue-400 text-sm hover:from-blue-600/30 hover:to-purple-600/30 hover:border-blue-400/50 transition-all active:scale-[0.98]"
                                  >
                                    <span className="flex items-center space-x-2">
                                      {getActionIcon(action.tool, action.icon)}
                                      <span className="font-medium">{action.label}</span>
                                    </span>
                                    <ArrowRight size={14} className="opacity-60" />
                                  </motion.button>
                                ))}
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
              
              {/* Loading indicator */}
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="flex items-start space-x-2">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                      <Robot size={14} className="text-white" weight="fill" />
                    </div>
                    <div className="px-3 py-2.5 bg-white/10 rounded-2xl">
                      <div className="flex items-center space-x-2">
                        <Spinner size={14} className="text-blue-400 animate-spin" />
                        <span className="text-slate-400 text-sm">Thinking...</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} className="h-1" />
            </div>

            {/* Input Area - Fixed positioning support for mobile */}
            <div 
              className="p-3 border-t border-white/10 bg-slate-900/80 backdrop-blur-sm flex-shrink-0"
              style={{ 
                paddingBottom: isMobile ? 'max(0.75rem, env(safe-area-inset-bottom))' : '0.75rem'
              }}
            >
              <div className="flex items-end space-x-2">
                <div className="flex-1 relative">
                  <textarea
                    ref={inputRef}
                    value={message}
                    onChange={(e) => {
                      setMessage(e.target.value);
                      // Round 107 — auto-grow the composer up to
                      // a sensible ceiling so long questions stay
                      // fully visible on mobile.
                      const el = e.target;
                      el.style.height = 'auto';
                      const max = window.innerWidth < 768 ? 240 : 200;
                      el.style.height = Math.min(el.scrollHeight, max) + 'px';
                    }}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask me anything..."
                    className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm min-h-[120px] md:min-h-[44px] leading-relaxed"
                    rows={3}
                    data-testid="chat-input"
                    style={{ fontSize: '16px' }} // Prevents iOS zoom on focus
                  />
                </div>
                <button
                  onClick={handleSend}
                  disabled={!message.trim() || isLoading}
                  data-testid="chat-send"
                  className="p-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl transition-all flex-shrink-0 active:scale-95 self-end mb-0"
                >
                  <PaperPlaneTilt size={18} weight="fill" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AskPayGBot;
